<template>
    <div
        :class="getWrapperClass"
        :tabindex="monthlyOnly ? 0 : undefined"
        :role="monthlyOnly ? 'button' : undefined"
        :aria-pressed="monthlyOnly ? isSelected : undefined"
        :aria-label="monthlyOnly ? planCardAriaLabel : undefined"
        @click="handlePlanPreSelect"
        @keydown="handleCardKeydown">
        <div :class="getBoxClassList">
            <div
                class="selection-bubble"
                v-if="monthlyOnly"
                :class="{ selected: isSelected }"
                aria-hidden="true">
                <svg
                    v-if="isSelected"
                    class="check-icon"
                    viewBox="0 0 32 32"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    focusable="false"
                    aria-hidden="true">
                    <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M16 32C24.8366 32 32 24.8366 32 16C32 7.16344 24.8366 0 16 0C7.16344 0 0 7.16344 0 16C0 24.8366 7.16344 32 16 32ZM24.2332 12.9428C24.7539 12.4221 24.7539 11.5779 24.2332 11.0572C23.7125 10.5365 22.8682 10.5365 22.3475 11.0572L14.2152 19.1895L10.9428 15.9171C10.4221 15.3964 9.57789 15.3964 9.05719 15.9171C8.53649 16.4378 8.53649 17.282 9.05719 17.8027L13.2724 22.018C13.5225 22.268 13.8616 22.4085 14.2152 22.4085C14.5688 22.4085 14.908 22.268 15.158 22.018L24.2332 12.9428Z"
                        fill="#0037C5" />
                </svg>
            </div>

            <!-- Coupon  -->
            <PlanCoupon
                :hasValidCoupon="hasValidCoupon()"
                :couponString="getCouponString()"
                :ftcCompliancePickAPlanEnabled="ftcCompliancePickAPlanEnabled" />
            <template v-if="ftcCompliancePickAPlanEnabled">
                <div :class="['plan-box-details', getPlanBoxDetailsClass()]">
                    <h3 v-html="planHeading"></h3>

                    <!-- START TEST PLOCTOPLUS-2035: Simplified Plans Tiles for POPI -->
                    <div v-if="isSimplifiedPlansPOPI" class="plan-prefix">
                        {{ planHeadingPrefix }}
                    </div>
                    <!-- END TEST PLOCTOPLUS-2035: Simplified Plans Tiles for POPI -->

                    <div v-if="getTrialCopy" :class="['plan-copy', getTrialCopyClass()]">
                        {{ getTrialCopy }}
                    </div>
                    <div
                        v-else-if="hasSingleTrialPlan && !getTrialCopy"
                        class="horizontal__divider" />

                    <div class="plan-price-copy" :class="getNonTrialPlanPriceClass()">
                        <!-- START TEST PLOCTOPLUS-2035: Simplified Plans Tiles for POPI -->
                        <span class="plan-price" v-if="isSimplifiedPlansPOPI">
                            {{ planPriceValue }}
                        </span>
                        <span class="plan-price-suffix" v-if="isSimplifiedPlansPOPI">
                            {{ planPriceSuffix }}
                        </span>
                        <!-- END TEST PLOCTOPLUS-2035: Simplified Plans Tiles for POPI -->
                        <span>{{ planPriceCopy }}</span>
                    </div>
                    <div
                        v-if="this.plan.isCouponValidAndAllowed()"
                        :class="{ 'plan-after-promo-copy': monthlyOnly }">
                        <span>
                            {{ afterPromoDescription }}
                        </span>
                    </div>
                    <div v-if="isAnnualPlan && !this.plan.isCouponValidAndAllowed()">
                        {{ monthlyEffectivePriceCopy }}
                    </div>
                    <div
                        v-else-if="hasSingleCouponPlan && noEftAndValidCoupon"
                        class="after-promo-description-spacer"></div>
                </div>
            </template>
            <template v-else>
                <div class="plan-box-details">
                    <h3 v-html="planHeading"></h3>
                    <!-- START TEST PLOCTOPLUS-2035: Simplified Plans Tiles for POPI -->
                    <div v-if="isSimplifiedPlansPOPI" class="plan-prefix">
                        {{ planHeadingPrefix }}
                    </div>
                    <!-- END TEST PLOCTOPLUS-2035: Simplified Plans Tiles for POPI -->

                    <div v-if="getTrialCopy" class="plan-copy">
                        {{ getTrialCopy }}
                    </div>

                    <div class="plan-price-copy" :class="getNonTrialPlanPriceClass()">
                        <span
                            class="copy-stripe"
                            v-if="plan.isCouponValidAndAllowed() && !ftcCompliancePickAPlanEnabled">
                            {{ plan.currencySymbol }}{{ plan.rawPrice }}
                        </span>

                        <!-- START TEST PLOCTOPLUS-2035: Simplified Plans Tiles for POPI -->
                        <span class="plan-price" v-if="isSimplifiedPlansPOPI">
                            {{ planPriceValue }}
                        </span>
                        <span class="plan-price-suffix" v-if="isSimplifiedPlansPOPI">
                            {{ planPriceSuffix }}
                        </span>
                        <!-- END TEST PLOCTOPLUS-2035: Simplified Plans Tiles for POPI -->

                        <span>{{ planPriceCopy }}</span>
                    </div>
                    <div v-if="isAnnualPlan">
                        {{ monthlyEffectivePriceCopy }}
                    </div>
                </div>
            </template>
            <template v-if="displayFeatureList">
                <hr :class="getPillClass" />
                <PillComponent
                    v-if="needsBestValuePill"
                    :className="getPillClass"
                    :textValue="planValuePillCopy"></PillComponent>

                <!-- START TEST PLOCTOPLUS-2035: Simplified Plans Tiles for POPI -->
                <FeatureList
                    :features="simplifiedPlansTilesList"
                    :plan="plan"
                    :bottomFeatures="bottomFeatures"
                    v-if="isSimplifiedPlansPOPI && !plansWithNoProps"></FeatureList>
                <!-- END TEST PLOCTOPLUS-2035: Simplified Plans Tiles for POPI -->
                <!-- PLOCTOPLUS-2035 v-else-if="!isSimplifiedPlansPOPI"
                line ~47 is part of a/b test, added only for a/b test and will remove when we will remove this a/b test -->
                <FeatureList
                    v-else-if="!isSimplifiedPlansPOPI"
                    :plan="plan"
                    :features="featureList"
                    :bottomFeatures="bottomFeatures"></FeatureList>
            </template>

            <ButtonCta v-if="!monthlyOnly" :buttonConfig="getButtonConfig"></ButtonCta>
        </div>

        <!-- START TEST PLOCTOPLUS-2035: Simplified Plans Tiles for POPI -->
        <div
            class="pick-a-plan-grid-item no-ads-disclaimer"
            v-if="isSimplifiedPlansPOPI && plan.isCF">
            *Except live TV and a few shows
        </div>
        <!-- END TEST PLOCTOPLUS-2035: Simplified Plans Tiles for POPI -->
    </div>
</template>

<script>
    import { mapGetters, mapState } from 'vuex';
    import PageAttributes from 'aa/vue/models/PageAttributes';
    import { PaymentConfig, PickAPlanConfig } from 'aa/vue/FormConfig';
    import ButtonCta from 'atoms/ButtonCta';
    import FeatureList from 'aa/vue/atoms/FeatureList';
    import { FLOW_TYPE } from 'models/FlowModel';
    import PillComponent from 'aa/vue/components/PillComponent';
    import PlanCoupon from 'aa/vue/components/PlanCoupon';
    import Feature, {
        CAMPAIGN_SERVICE_ENABLED,
        CONTINUE_FREE_TRIAL,
        FTC_COMPLIANCE_PICKAPLAN_ENABLED,
    } from 'aa/helpers/featureConstants';
    import aarpMixin from 'aa/vue/plugins/aarpMixin';
    import couponsMixin from 'aa/vue/plugins/couponsMixin';

    //START TEST PLOCTOPLUS-2035: Simplified Plans Tiles for POPI
    import SimplifiedPlansTilesAbTestPOPI from 'aa/vue/abTestRelated/SimplifiedPlansTilesAbTestPOPI';
    //END TEST PLOCTOPLUS-2035: Simplified Plans Tiles for POPI
    // START TEST PLOCTOPLUS-2036: plan tiles above fold
    import AbTestPlanTilesAboveFoldMixin from 'aa/vue/abTestRelated/AbTestPlanTilesAboveFoldMixin';
    import Plan from 'aa/vue/models/Plan';
    // END TEST PLOCTOPLUS-2036: plan tiles above fold
    import {
        getOfferPriceString,
        getDurationString,
        getAfterPromoString,
    } from 'aa/vue/helpers/coupon.helpers';
    import { getLocale } from 'services/LocalizationService';

    export default {
        name: 'PickAPlanBox',
        mixins: [
            aarpMixin,
            couponsMixin,

            //START TEST PLOCTOPLUS-2035: Simplified Plans Tiles for POPI
            SimplifiedPlansTilesAbTestPOPI,
            //END TEST PLOCTOPLUS-2035: Simplified Plans Tiles for POPI

            // START TEST PLOCTOPLUS-2036: plan tiles above fold
            AbTestPlanTilesAboveFoldMixin,
            // END TEST PLOCTOPLUS-2036: plan tiles above fold
        ],

        props: {
            idx: {
                type: Number,
            },
            plan: {
                type: Plan,
                default: {
                    trialInterval: '',
                    trialUnit: '',
                    tier: '',
                    trial: true,
                },
            },
            planValuePillCopy: {
                type: String,
                required: false,
            },
            isNoFreeTrialCampaign: {
                type: Boolean,
            },
            hasSingleTrialPlan: {
                type: Boolean,
                default: false,
            },

            hasSingleCouponPlan: {
                type: Boolean,
                default: false,
            },

            ftcCompliancePickAPlanEnabled: {
                type: Boolean,
                default: false,
            },

            isActivePlan: {
                type: Boolean,
                required: false,
                default: () => false,
            },
            monthlyOnly: {
                type: Boolean,
                required: false,
                default: false,
            },
            // START TEST: PPIWEBM-2699 Essential plan prioritization
            isEssentialPlanPrioritizationVariant: {
                type: Boolean,
                required: false,
                default: () => false,
            },
            // END TEST: PPIWEBM-2699 Essential plan prioritization
        },
        event: 'planSelected',
        data: function () {
            return {
                coupon: {
                    discount: {
                        type: '',
                        trial: {
                            amount: 0,
                        },
                    },
                },
                papConfig: PickAPlanConfig,
                buttonConfig: {},
            };
        },
        components: {
            FeatureList,
            ButtonCta,
            PillComponent,
            PlanCoupon,
        },
        beforeCreate() {
            // ignore IDE "unresolved variable"
            PaymentConfig.recurlyConfig.publicKey = this.$root.serverData.recurlyPublicKey;
        },
        mounted() {
            // runs when XHR to get plans completes and components are rendered
            // trigger recurly
            this.$nextTick(() => {
                if (this.recurly) {
                    this.updatePriceAndCouponCopy(this.plan, this.$store.state.promo);
                }
                if (this.monthlyOnly && this.isActivePlan) {
                    this.selectPlanCard();
                }
            });
        },
        methods: {
            // emit the selected plan
            handleClick: function () {
                let ctaText = this.buttonConfig.displayText;
                this.$emit('planSelected', this.plan, ctaText);
            },

            // replaces the trial copy for the plans if a coupon is associated with the user flow

            hasValidCoupon: function () {
                if (this.isAarpFlow()) {
                    return this.plan.hasValidCoupon() && this.hasValidAarpCoupon();
                }

                if (this.isPickPlanTierPreselectionEnabled && this.annualHasValidCoupon()) {
                    return true;
                }

                return this.plan.hasValidCoupon();
            },

            getCouponString() {
                if (this.isPickPlanTierPreselectionEnabled) {
                    return this.getGenericPromoCopy();
                }
                return this.plan.getCouponString();
            },

            // checks if there is a valid coupon for the annual plan of the same tier to determine
            // if we should show generic promo copy
            annualHasValidCoupon: function () {
                const sameTierAnnualPlan = this.annualPlans.find(
                    (annualPlan) => annualPlan.tier === this.plan.tier,
                );
                return sameTierAnnualPlan.couponIsValid;
            },

            getGenericPromoCopy() {
                if (this.isPickPlanTierPreselectionEnabled && this.annualHasValidCoupon()) {
                    return this.pageAttrs.aa_pap_generic_promo_copy;
                }
                return this.plan.getCouponString();
            },

            dspAfterTrial() {
                return;
            },

            getPlanBoxDetailsClass: function () {
                return {
                    'plan-box-bottom-margin':
                        this.hasSingleCouponPlan &&
                        !this.plan.isCouponValidAndAllowed() &&
                        this.ftcCompliancePickAPlanEnabled,
                };
            },

            //adds conditional css depending on if the plan has trial copy
            getNonTrialPlanPriceClass: function () {
                return {
                    'nontrial-plan-price':
                        this.hasSingleTrialPlan &&
                        !this.getTrialCopy &&
                        !this.ftcCompliancePickAPlanEnabled,
                    'price-green':
                        this.ftcCompliancePickAPlanEnabled && this.plan.isCouponValidAndAllowed(),
                    'percent-coupon-plan-price':
                        this.ftcCompliancePickAPlanEnabled && this.plan.isCouponValidAndAllowed(),
                };
            },

            getTrialCopyClass: function () {
                return {
                    'plan-copy-green':
                        this.plan?.couponType === 'free_trial' &&
                        this.ftcCompliancePickAPlanEnabled,
                };
            },

            selectPlanCard() {
                const ctaText = this.monthlyOnly
                    ? this.getButtonText()
                    : this.getButtonConfig.displayText;
                this.$emit('handleCardSelected', {
                    idx: this.idx,
                    ctaText,
                    plan: this.plan,
                });
            },
            handleCardKeydown(e) {
                if (!this.monthlyOnly) return;
                if (e.key !== 'Enter' && e.key !== ' ') return;
                e.preventDefault();
                this.selectPlanCard();
            },
            handlePlanPreSelect(e) {
                if (this.monthlyOnly && e?.type === 'click') {
                    this.selectPlanCard();
                }
            },

            getButtonText() {
                if (
                    Feature.enabled(CAMPAIGN_SERVICE_ENABLED) &&
                    Feature.enabled(CONTINUE_FREE_TRIAL)
                ) {
                    if (this.getTrialCopy) {
                        return this.pageAttrs?.aa_pap_button_text_continue_free_trial ?? '';
                    } else if (this.isExSubscriber || !this.plan?.isFreeTrial) {
                        return this.pageAttrs?.aa_pap_button_text_continue ?? '';
                    }
                }
                return this.pageAttrs.aa_pap_button_text;
            },
        },
        computed: {
            isSelected() {
                if (this.monthlyOnly) {
                    return Boolean(this.isActivePlan);
                }

                const globalSelectedPlan = this.$store.getters['plan/getSelectedPlan'];
                if (globalSelectedPlan && this.plan) {
                    return globalSelectedPlan.recurlyCode === this.plan.recurlyCode;
                }
                return false;
            },
            planCardAriaLabel() {
                const name = this.plan?.planTitle || 'plan';
                return `Select ${name}`;
            },
            getWrapperClass() {
                return {
                    'plan-box-wrapper': true,
                    'selected-plan': this.isSelected,
                };
            },
            getBoxClassList() {
                return {
                    'plan-box': true,
                    'monthly-only': this.monthlyOnly,
                };
            },
            getPillClass() {
                return this.needsBestValuePill ? 'best-value' : '';
            },
            needsBestValuePill() {
                // START TEST: PPIWEBM-2699 Essential plan prioritization
                if (this.isEssentialPlanPrioritizationVariant) {
                    return this.plan?.tier === 0;
                }
                // END TEST: PPIWEBM-2699 Essential plan prioritization
                return this.plan.isCF;
            },
            recurly() {
                return this.$store.state.payment.recurly;
            },
            noEftAndValidCoupon() {
                return (
                    !this.plan?.hasValidEftCoupon(this.isExSubscriber) &&
                    !this.plan.isCouponValidAndAllowed()
                );
            },

            planPriceCopy() {
                let price = this.plan.price.toLowerCase();
                let pa;

                // grouped plan cards use the abbreviated unit (mo/yr), the same string the coupon copy uses
                if (this.plan.planGroup) {
                    const unit = this.isAnnualPlan
                        ? getLocale('year_abbr', {}, 'yr')
                        : getLocale('month_abbr', {}, 'mo');
                    price = price.replace(/\/.*$/, `/${unit}`);
                }

                if (this.plan.isCouponValidAndAllowed()) {
                    const shortenedTerm = this.isAnnualPlan
                        ? getLocale('year_abbr', {}, 'yr')
                        : getLocale('month_abbr', {}, 'mo');

                    const offerString = getOfferPriceString(
                        this.plan.currencySymbol,
                        this.plan.couponDiscount.priceSubtotal,
                        this.plan.priceUnit,
                        shortenedTerm,
                        this.plan.config.recurlyCoupon,
                        this.ftcCompliancePickAPlanEnabled,
                        this.isAnnualPlan,
                        this.plan.currencySubunits,
                    );
                    const durationString = getDurationString(
                        this.plan.config.recurlyCoupon,
                        this.plan.priceUnit,
                        this.isAnnualPlan,
                        this.ftcCompliancePickAPlanEnabled,
                    );

                    return `${offerString}${durationString}`;
                }

                if (this.plan?.hasValidEftCoupon(this.isExSubscriber)) {
                    if (this.monthlyOnly) {
                        return price;
                    }
                    return price + ' ' + getLocale('after_trial', {}, 'after trial');
                }

                if (!this.getTrialCopy) {
                    return price;
                }

                if (this.plan.couponType) {
                    pa = this.pageAttrs.aa_pap_plan_price_format_with_percent_coupon;
                } else if (!this.plan.trial) {
                    pa = this.pageAttrs.aa_pap_plan_price_format_no_trial;
                } else {
                    pa = this.pageAttrs.aa_pap_plan_price_format_with_trial;
                }

                return pa.replace('%PRICE_WITH_UNIT%', price);
            },

            afterPromoDescription() {
                const shortenedTerm = this.isAnnualPlan
                    ? getLocale('year_abbr', {}, 'yr')
                    : getLocale('month_abbr', {}, 'mo');
                return getAfterPromoString(
                    this.plan.config.recurlyCoupon,
                    this.plan.currencySymbol,
                    this.plan.rawPrice,
                    shortenedTerm,
                    this.ftcCompliancePickAPlanEnabled,
                    this.isExSubscriber ? false : this.plan.trial,
                );
            },

            planHeading() {
                return this.plan.planTitle;
            },

            eligibleForCoupon() {
                return !(this.flow.type === FLOW_TYPE.GIFT);
            },

            noCouponApplied() {
                return !this.eligibleForCoupon || !this.plan.couponString;
            },

            removeBaseTrial() {
                const isNoFreeTrialCampaign = Feature.enabled(CAMPAIGN_SERVICE_ENABLED)
                    ? false
                    : this.isNoFreeTrialCampaign;

                return (
                    this.isNoTrialUser ||
                    this.flow.type === FLOW_TYPE.GIFT ||
                    isNoFreeTrialCampaign ||
                    this.plan.couponRemovesTrial === true
                );
            },

            waitForCouponCheck() {
                return (
                    this.$store.state.promo &&
                    this.eligibleForCoupon &&
                    this.plan.couponRemovesTrial === null
                ); // pending validation
            },

            // refer to svod/Product.php -> getTrialString
            getTrialCopy: function () {
                if (this.isPickPlanTierPreselectionEnabled && this.plan?.isEftCoupon()) {
                    return this.formattedEftCouponString;
                }

                if (this.plan?.hasValidEftCoupon(this.isExSubscriber) && this.couponString) {
                    return this.couponString;
                }

                if (!this.plan.trial) {
                    return null;
                }

                if (
                    this.waitForCouponCheck ||
                    (this.removeBaseTrial && this.noCouponApplied) ||
                    !this.plan.trial
                ) {
                    return null;
                }

                this.plan.pageAttrs = this.pageAttrs;

                return (
                    this.plan
                        .setRemoveBaseTrial(this.removeBaseTrial)
                        .getTrialCopy(this.noCouponApplied, this.isExSubscriber) ?? ''
                );
            },

            isAnnualPlan() {
                return this.plan.isAnnual;
            },

            monthlyEffectivePriceCopy() {
                if (!this.isAnnualPlan) {
                    return null;
                }
                try {
                    return this.pageAttrs.aa_pap_annual_plan_monthly_price_format.replace(
                        '%MONTHLY_PRICE_WITH_UNIT%',
                        this.plan.annualPlanMonthlyPriceWithUnit(),
                    );
                } catch (e) {
                    return null;
                }
            },

            keyPrefixPackage() {
                // a grouped plan is a product of its own: its copy is keyed by its package code
                if (this.plan.planGroup) {
                    return this.plan.package_code.toLowerCase();
                }
                return this.plan.isCF ? 'cf' : 'lcp';
            },

            /**
             * @returns {Array}
             */
            featureList() {
                let list = [];

                const keyPrefix = `aa_pap_${this.keyPrefixPackage}_feature_list_item_`;
                const keySuffix = this.plan.bundleShowtime ? PageAttributes.SHOWTIME_SUFFIX : '';

                new Array(10).fill().forEach((value, index) => {
                    let pageAttributeKey = `${keyPrefix}${index + 1}${keySuffix}`;

                    const listItem = this.pageAttrs[pageAttributeKey]?.trim();

                    if (listItem) {
                        list.push(listItem);
                    }
                });

                return list;
            },

            /**
             * @returns {Array}
             */
            bottomFeatures() {
                let list = [];

                const keyPrefix = `aa_pap_${this.keyPrefixPackage}_feature_list_bottom_item_`;

                new Array(10).fill().forEach((value, index) => {
                    let pageAttributeKey = `${keyPrefix}${index + 1}`;

                    const listItem = this.pageAttrs[pageAttributeKey]?.trim();

                    if (listItem) {
                        list.push(listItem);
                    }
                });

                return list;
            },

            displayFeatureList() {
                return this.featureList.length > 0;
            },

            getButtonConfig: function () {
                this.buttonConfig = Object.assign({}, this.papConfig.button);
                this.buttonConfig.displayText = this.getButtonText();
                this.buttonConfig.ariaLabel = this.$getLocale('select_plan_name', {
                    plan: this.plan.planTitle,
                });
                this.buttonConfig.event = this.handleClick;

                if (this.plan?.hasValidEftCoupon(this.isExSubscriber) && this.couponString) {
                    this.buttonConfig.displayText = getLocale('try') + ' ' + this.couponString;
                }

                return this.buttonConfig;
            },

            couponString() {
                return this.plan?.couponString ?? '';
            },

            formattedEftCouponString() {
                const couponFormat = this.pageAttrs.aa_pap_extended_free_trial_coupon_string || '';
                return couponFormat.replace('%s', this.plan?.couponString ?? '');
            },

            ...mapState('pageAttributes', {
                pageAttrs: (state) => state.attributes,
            }),
            ...mapGetters('user', ['isNoTrialUser']),
            ...mapState('flow', {
                flow: (state) => state.config,
            }),
            ...mapState('user', {
                isExSubscriber: (state) => state.isExSubscriber,
            }),
            ...mapGetters({
                annualPlans: 'plan/annualPlans',
                bundleShowtime: 'bundleShowtime',
                featureFlagIsActive: 'featureFlags/isActive',
                isPickPlanTierPreselectionEnabled: 'featureFlags/isPickPlanTierPreselectionEnabled',
            }),
        },
        watch: {
            plan: function () {
                this.updatePriceAndCouponCopy(this.plan, this.$store.state.promo);
            },
            bundleShowtime(newVal, oldVal) {
                this.updatePriceAndCouponCopy(this.plan, this.$store.state.promo);
            },
            recurly(newVal, oldVal) {
                if (newVal) {
                    this.updatePriceAndCouponCopy(this.plan, this.$store.state.promo);
                }
            },
        },
    };
</script>
